// The parts of a Findings case's story (components/CaseStory.jsx) that are
// rules rather than drawing: how its actors are grouped, how many of its
// steps each took part in, which to fold away when the list runs past
// the story beside it, and how a pause between steps is said.

/** The groups the actors are listed under, in order, and the kinds each holds. */
export const ACTOR_GROUPS = [
    ["Assigned identities", ["identity"]],
    ["Sources", ["user", "endpoint", "trace"]],
    ["Agents", ["agent"]],
    ["Models", ["model"]],
    ["Tools", ["mcp", "skill", "tool"]],
];

const ENTITIES = new Set(["identity", "user", "endpoint"]);

/** `actors` ([{ id, kind }]) under their groups: [{ label, actors }], empty groups left out. */
export function groupActors(actors) {
    return ACTOR_GROUPS
        .map(([label, kinds]) => ({ label, actors: actors.filter((a) => kinds.includes(a.kind)) }))
        .filter((g) => g.actors.length);
}

/** How many of `steps` ([{ actors: [id] }]) each actor took part in: Map(id -> n). */
export function stepCounts(steps) {
    const n = new Map();
    for (const s of steps) for (const id of new Set(s.actors || [])) n.set(id, (n.get(id) || 0) + 1);
    return n;
}

/**
 * The next actor to fold away while the list is too long. First into its
 * group's "N more": from the group showing the most, its least involved, the
 * later one on a tie, people and endpoints only once nothing else is left to
 * fold, until every group shows one. Then whole groups, the
 * last first, each down to its heading alone. `groups` as groupActors gives
 * them, `hidden` the ids folded so far, `countOf(id)` an actor's steps (null
 * when unknown, read as none). Null when everything is folded.
 */
export function nextToFold(groups, hidden, countOf) {
    const open = groups.map((g) => g.actors.filter((a) => !hidden.has(a.id)));
    const many = open.filter((g) => g.length > 1);
    if (many.length) {
        const n = (a) => countOf(a.id) ?? 0;
        // Who and which endpoints fold after everything else, so a case that
        // reaches several of them shows several.
        const spare = many.map((g) => g.filter((a) => !ENTITIES.has(a.kind))).filter((g) => g.length);
        const pool = spare.length ? spare : many;
        const most = pool.reduce((m, g) => (g.length >= m.length ? g : m));
        return most.reduce((m, a) => (n(a) <= n(m) ? a : m)).id;
    }
    for (let i = open.length - 1; i >= 0; i -= 1) if (open[i].length) return open[i][0].id;
    return null;
}

/** A pause between two steps, as a person would say it: minutes, then whole hours, days and weeks, never a fraction. */
export function pauseText(ms) {
    const min = Math.round(ms / 60000);
    if (min < 60) return `${min} minute${min === 1 ? "" : "s"} later`;
    const h = Math.round(min / 60);
    if (h < 24) return h === 1 ? "an hour later" : `${h} hours later`;
    const d = Math.round(h / 24);
    if (d < 14) return d === 1 ? "a day later" : `${d} days later`;
    return `${Math.round(d / 7)} weeks later`;
}
